import React, { useState, useEffect } from "react";
import "../common/common.css";
import "./loginLanding.css";
import { useAuth } from "./AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { loadAppConfig, getAppConfig } from "../../utils/appConfig";
import brandLogo from "../../assets/login/brand-logo.svg";
import heroIllustration from "../../assets/login/hero-illustration.svg";
import featureSearch from "../../assets/login/feature-search.svg";
import featureFlow from "../../assets/login/feature-flow.svg";
import featureRich from "../../assets/login/feature-rich.svg";
import tipAvatar from "../../assets/login/tip-avatar.svg";

export function LoginScreen() {
  const { t } = useTranslation();
  const {
    login,
    windowsSsoLogin,
    checkWindowsSsoStatus,
    loading,
    savedCredentialsKey,
    isAuthenticated,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromLocation = location.state?.from;
  const from = fromLocation
    ? (fromLocation.pathname || "/") +
      (fromLocation.search || "") +
      (fromLocation.hash || "")
    : "/";
  const initialCreds = (() => {
    try {
      if (savedCredentialsKey) {
        const raw = localStorage.getItem(savedCredentialsKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          return {
            email: parsed?.email || "",
            password: parsed?.password
              ? (() => {
                  try {
                    return atob(parsed.password);
                  } catch {
                    return "";
                  }
                })()
              : "",
          };
        }
      }
    } catch {}
    return { email: "", password: "" };
  })();
  const [email, setEmail] = useState(initialCreds.email);
  const [password, setPassword] = useState(initialCreds.password);
  const [error, setError] = useState("");
  const [remember, setRemember] = useState(() => {
    if (initialCreds.email || initialCreds.password) return true;
    return true; // default on first load
  });

  // On mount, attempt to prefill last remembered credentials
  useEffect(() => {
    try {
      if (savedCredentialsKey && localStorage.getItem(savedCredentialsKey)) {
        const parsed = JSON.parse(localStorage.getItem(savedCredentialsKey));
        if (parsed?.email) setEmail(parsed.email);
        if (parsed?.password) {
          try {
            setPassword(atob(parsed.password));
          } catch {
            /* ignore */
          }
        }
      }
    } catch {
      /* ignore */
    }
  }, [savedCredentialsKey]);
  useEffect(() => {
    // If credentials were cleared elsewhere (e.g., storage manually cleared), do not overwrite user edits.
  }, [savedCredentialsKey]);
  const [submitting, setSubmitting] = useState(false);
  const [windowsSsoAvailable, setWindowsSsoAvailable] = useState(false);
  const [windowsSsoLoading, setWindowsSsoLoading] = useState(false);
  const [appConfig, setAppConfig] = useState(getAppConfig);

  useEffect(() => {
    loadAppConfig().then(setAppConfig);
  }, []);

  // Check if Windows SSO is available on mount
  useEffect(() => {
    (async () => {
      try {
        const status = await checkWindowsSsoStatus();
        setWindowsSsoAvailable(status.available);
      } catch {
        setWindowsSsoAvailable(false);
      }
    })();
  }, [checkWindowsSsoStatus]);

  // If already authenticated (e.g., restored from storage), skip login screen
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [loading, isAuthenticated, from, navigate]);

  if (loading) return <div className="page-loading">{t("login.loading")}</div>;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password, remember);
      // navigate to originally requested page
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const onWindowsSsoLogin = async () => {
    setError("");
    setWindowsSsoLoading(true);
    try {
      await windowsSsoLogin(remember);
      // navigate to originally requested page
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || "Windows SSO login failed");
    } finally {
      setWindowsSsoLoading(false);
    }
  };

  return (
    <div className="landing-root">
      <div className="landing-hero">
        <div className="hero-top">
          <div className="brand">
            <img src={brandLogo} alt="DIXI Vault" className="brand-logo" />
            <span className="brand-name">DIXI Vault</span>
          </div>
          <a
            className="link subtle"
            href="/docs/"
            target="_blank"
            rel="noreferrer"
          >
            Docs
          </a>
        </div>

        <div className="hero-content">
          <div className="hero-copy">
            <div className="eyebrow">{t("login.eyebrow")}</div>
            <h1>{t("login.headline")}</h1>
            <p>
              Run structured searches, group results by database, and inspect
              rich metadata for each document. Use the Flow Designer and Runner
              to build simple processing pipelines when needed. Optional
              LLM‑assisted filter interpretation is available.
            </p>
            {(appConfig.showFreeTrial || appConfig.showViewRepo) && (
              <div className="cta-row">
                {appConfig.showFreeTrial && (
                  <a className="cta primary" href="/signup">
                    {t("login.startTrial")}
                  </a>
                )}
                {appConfig.showViewRepo && (
                  <a
                    className="cta ghost"
                    href="https://github.com/VolkanVural/DEPOSITORY.API"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("login.viewRepo")}
                  </a>
                )}
              </div>
            )}
            <div className="proof-row" aria-label={t("login.availableModules")}>
              <span className="ai-chip">{t("login.module.search")}</span>
              <span className="ai-chip">{t("login.module.flowDesigner")}</span>
              <span className="ai-chip">{t("login.module.flowRunner")}</span>
            </div>
          </div>
          <div className="hero-art">
            <img src={heroIllustration} alt="Unified document control" />
          </div>
        </div>

        <div className="features">
          <div className="feature">
            <img src={featureSearch} alt={t("login.fastSearch.alt")} />
            <h3>{t("login.fastSearch.title")}</h3>
            <p>{t("login.fastSearch.desc")}</p>
          </div>
          <div className="feature">
            <img src={featureFlow} alt={t("login.flow.alt")} />
            <h3>{t("login.flow.title")}</h3>
            <p>{t("login.flow.desc")}</p>
          </div>
          <div className="feature">
            <img src={featureRich} alt={t("login.rich.alt")} />
            <h3>{t("login.rich.title")}</h3>
            <p>{t("login.rich.desc")}</p>
          </div>
        </div>
      </div>

      <aside className="auth-panel" aria-label="Sign in">
        <form onSubmit={onSubmit} className="auth-card">
          <div className="chip">{t("login.welcome")}</div>
          <label className="f-label">
            {t("login.email")}
            <input
              className="f-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("login.emailPlaceholder")}
              autoComplete="username"
            />
          </label>
          <label className="f-label">
            {t("login.password")}
            <input
              className="f-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("login.passwordPlaceholder")}
              autoComplete="current-password"
            />
          </label>
          <label className="remember">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
            />
            <span>{t("login.remember")}</span>
          </label>
          {error && (
            <div className="error-text" role="alert">
              {error}
            </div>
          )}
          <button className="cta submit" disabled={submitting}>
            {submitting ? t("login.signingIn") : t("login.signIn")}
          </button>

          {windowsSsoAvailable && (
            <>
              <div className="auth-divider">
                <span>{t("login.or", "or")}</span>
              </div>
              <button
                type="button"
                className="cta windows-sso"
                disabled={windowsSsoLoading}
                onClick={onWindowsSsoLogin}
              >
                <svg
                  className="windows-icon"
                  viewBox="0 0 21 21"
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                >
                  <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                </svg>
                {windowsSsoLoading
                  ? t("login.windowsSsoLoading", "Signing in with Windows…")
                  : t("login.windowsSso", "Sign in with Windows")}
              </button>
            </>
          )}

          <div className="small-links">
            <a href="/forgot-password">{t("login.forgot")}</a>
            {appConfig.showFreeTrial && (
              <a href="/signup">{t("login.createAccount")}</a>
            )}
          </div>
        </form>

        <div className="quote" aria-label={t("login.tipLabel")}>
          <img src={tipAvatar} alt="" className="avatar" />
          <blockquote>{t("login.tipText")}</blockquote>
          <div className="cite">{t("login.productTip")}</div>
        </div>
      </aside>
    </div>
  );
}

// Inline styles replaced by CSS module 'loginLanding.css' for maintainability.
